import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../hooks/useAuth';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import toast from '../utils/toastUtils';
import SEO from '../components/SEO';

// ── Pricing Plans ─────────────────────────────────────────────────────────────
const PLANS = [
  {
    name: 'Sky Starter',
    description: 'Perfect for light personal use',
    storage: '15 GB',
    monthlyPrice: 3.99,
    annualMonthlyPrice: 3.39,
    annualPrice: 40.68,
    annualDiscount: 15,
    monthlyPriceId: 'price_1TfGfODO4NhXMSBB9vRwGtaK',
    annualPriceId: 'price_1TfGfODO4NhXMSBBbDOkhtIL',
    recommended: false,
    features: [
      '15 GB secure cloud storage',
      'Up to 1 GB per file',
      'Unlimited file uploads',
      'Secure file sharing with 24hr links',
      'Folder organization & favorites',
      '30-day trash recovery',
      'Email support',
      'All future features included',
    ],
  },
  {
    name: 'Sky One',
    description: 'Everything you need for personal cloud storage',
    storage: '50 GB',
    monthlyPrice: 6.99,
    annualMonthlyPrice: 5.59,
    annualPrice: 67.08,
    annualDiscount: 20,
    monthlyPriceId: 'price_1TfGkhDO4NhXMSBBi3lsgICw',
    annualPriceId: 'price_1TKGoPDO4NhXMSBBy06TpSB9',
    recommended: true,
    features: [
      '50 GB secure cloud storage',
      'Up to 3 GB per file',
      'Unlimited file uploads',
      'Secure file sharing with 24hr links',
      'Folder organization & favorites',
      '30-day trash recovery',
      'Email support',
      'All future features included',
    ],
  },
  {
    name: 'Sky Pro',
    description: 'For power users and small businesses',
    storage: '200 GB',
    monthlyPrice: 11.99,
    annualMonthlyPrice: 8.99,
    annualPrice: 107.88,
    annualDiscount: 25,
    monthlyPriceId: 'price_1TfGj5DO4NhXMSBBbsKUHSPV',
    annualPriceId: 'price_1TfGj5DO4NhXMSBBNmkvSL8Z',
    recommended: false,
    features: [
      '200 GB secure cloud storage',
      'Up to 10 GB per file',
      'Unlimited file uploads',
      'Secure file sharing with 24hr links',
      'Folder organization & favorites',
      '30-day trash recovery',
      'Priority email support',
      'All future features included',
    ],
  },
];

export default function Pricing() {
  const { theme } = useTheme();
  const { userId, isAuthenticated } = useAuth();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  useEffect(() => {
    const wantsToSubscribe = sessionStorage.getItem('wantsToSubscribe');
    const savedPriceId = sessionStorage.getItem('selectedPriceId');
    if (isAuthenticated && wantsToSubscribe === 'true' && savedPriceId) {
      if (userId) {
        sessionStorage.removeItem('wantsToSubscribe');
        sessionStorage.removeItem('selectedPriceId');
        handleSubscribe(savedPriceId);
      }
    }
  }, [isAuthenticated, userId]);

  const handleSubscribe = async (priceId: string) => {
    if (!isAuthenticated) {
      sessionStorage.setItem('wantsToSubscribe', 'true');
      sessionStorage.setItem('selectedPriceId', priceId);
      toast.info('Redirecting to sign in...', 2000);
      setTimeout(() => {
        instance.loginRedirect({ scopes: ['openid', 'profile'] });
      }, 500);
      return;
    }

    sessionStorage.removeItem('wantsToSubscribe');
    sessionStorage.removeItem('selectedPriceId');

    if (!userId) {
      toast.error('Authentication incomplete. Please try again.');
      return;
    }

    setIsLoading(priceId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_CREATE_CHECKOUT_URL}&userId=${encodeURIComponent(userId)}&priceId=${encodeURIComponent(priceId)}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );

      // ── Geo-blocked ───────────────────────────────────────────
      if (response.status === 403) {
        const data = await response.json();
        if (data.error === 'geo_blocked') {
          toast.error('Sorry, Trubonn is currently only available in Australia.', 6000);
          setIsLoading(null);
          return;
        }
      }

      if (!response.ok) throw new Error('Failed to create checkout session');

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setIsLoading(null);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.bgPrimary,
      color: theme.colors.textPrimary,
    }}>
      <SEO
        title="Pricing — Australian Cloud Storage Plans | Trubonn"
        description="Simple, transparent pricing for Australian-hosted cloud storage. Plans from 15 GB to 200 GB, all with a 7-day free trial, no data mining, and full Privacy Act compliance."
        path="/pricing"
      />

      {/* ── Public Nav Header ───────────────────────────────── */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        position: 'sticky',
        top: 0,
        background: theme.colors.bgPrimary,
        zIndex: 1000,
      }}>
        <div
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
        >
          <img src="/logo.svg" alt="Trubonn Logo" style={{ height: '70px', width: '180px' }} />
        </div>

        <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'transparent', border: 'none',
              color: theme.colors.textSecondary, cursor: 'pointer',
              fontSize: '16px', padding: '8px 16px', transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Home
          </button>

          {isAuthenticated ? (
            <button
              onClick={() => navigate('/home')}
              style={{
                background: theme.gradients.brand, border: 'none', color: '#fff',
                padding: '12px 24px', borderRadius: '8px', fontSize: '16px',
                fontWeight: 600, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(157, 78, 221, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Go to App
            </button>
          ) : (
            <button
              onClick={() => instance.loginRedirect({ scopes: ['openid', 'profile'] })}
              style={{
                background: theme.gradients.brand, border: 'none', color: '#fff',
                padding: '12px 24px', borderRadius: '8px', fontSize: '16px',
                fontWeight: 600, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(157, 78, 221, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Sign In
            </button>
          )}
        </div>
      </nav>

      {/* ── Page Content ────────────────────────────────────── */}
      <div style={{ padding: '60px 40px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontSize: '48px', fontWeight: 700,
            color: theme.colors.textPrimary, marginBottom: '16px',
          }}>
            Simple, transparent pricing
          </h1>
          <p style={{
            fontSize: '20px', color: theme.colors.textSecondary,
            maxWidth: '600px', margin: '0 auto 24px',
          }}>
            Start free for 7 days — no charge until Day 8.
          </p>

          {/* Trial badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: `${theme.colors.brand}15`,
            border: `1px solid ${theme.colors.brand}40`,
            borderRadius: '20px',
            padding: '8px 20px',
          }}>
            <span style={{ fontSize: '16px' }}>🎉</span>
            <span style={{ color: theme.colors.brand, fontSize: '14px', fontWeight: 600 }}>
              7-day free trial · No charge until Day 8 · Cancel anytime
            </span>
          </div>
        </div>

        {/* Billing toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: '12px',
          marginBottom: '48px',
        }}>
          <div style={{
            display: 'inline-flex',
            background: theme.colors.bgSecondary,
            border: `2px solid ${theme.colors.border}`,
            borderRadius: '16px',
            padding: '6px',
            gap: '4px',
          }}>
            <button
              onClick={() => setBillingCycle('monthly')}
              style={{
                padding: '12px 32px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: billingCycle === 'monthly' ? theme.gradients.brand : 'transparent',
                color: billingCycle === 'monthly' ? '#fff' : theme.colors.textSecondary,
              }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              style={{
                padding: '12px 32px',
                borderRadius: '12px',
                border: 'none',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: billingCycle === 'annual' ? theme.gradients.brand : 'transparent',
                color: billingCycle === 'annual' ? '#fff' : theme.colors.textSecondary,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Annual
              <span style={{
                background: '#10B98130',
                color: '#10B981',
                fontSize: '11px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '8px',
                letterSpacing: '0.3px',
              }}>
                SAVE UP TO 25%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px',
          marginBottom: '80px',
          alignItems: 'start',
        }}>
          {PLANS.map((plan) => {
            const priceId = billingCycle === 'annual' ? plan.annualPriceId : plan.monthlyPriceId;
            const displayPrice = billingCycle === 'annual' ? plan.annualMonthlyPrice : plan.monthlyPrice;
            const loading = isLoading === priceId;

            return (
              <div
                key={plan.name}
                style={{
                  padding: '32px',
                  background: theme.colors.bgSecondary,
                  borderRadius: '16px',
                  border: `2px solid ${plan.recommended ? theme.colors.brand : theme.colors.border}`,
                  position: 'relative',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(157, 78, 221, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Recommended badge */}
                {plan.recommended && (
                  <div style={{
                    position: 'absolute',
                    top: '-14px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: theme.gradients.brand,
                    color: '#fff',
                    padding: '4px 20px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    letterSpacing: '0.5px',
                  }}>
                    ⭐ MOST POPULAR
                  </div>
                )}

                {/* Plan name */}
                <h3 style={{
                  fontSize: '22px', fontWeight: 700,
                  color: theme.colors.textPrimary, marginBottom: '4px',
                  marginTop: plan.recommended ? '8px' : '0',
                }}>
                  {plan.name}
                </h3>
                <p style={{ fontSize: '13px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                  {plan.description}
                </p>

                {/* Storage badge */}
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: `${theme.colors.brand}15`,
                  border: `1px solid ${theme.colors.brand}30`,
                  borderRadius: '8px',
                  padding: '4px 10px',
                  marginBottom: '20px',
                }}>
                  <span style={{ fontSize: '12px' }}>☁️</span>
                  <span style={{ color: theme.colors.brand, fontSize: '13px', fontWeight: 600 }}>
                    {plan.storage}
                  </span>
                </div>

                {/* Price */}
                <div style={{
                  background: theme.colors.bgPrimary,
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '24px',
                  textAlign: 'center',
                }}>
                  <div style={{ marginBottom: '4px' }}>
                    <span style={{
                      fontSize: '48px',
                      fontWeight: 800,
                      background: theme.gradients.brand,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}>
                      ${displayPrice.toFixed(2)}
                    </span>
                    <span style={{ fontSize: '16px', color: theme.colors.textSecondary, marginLeft: '4px' }}>
                      AUD/mo
                    </span>
                  </div>

                  {billingCycle === 'annual' ? (
                    <>
                      <p style={{ color: theme.colors.textTertiary, fontSize: '12px', margin: '0 0 6px' }}>
                        Billed as <strong style={{ color: theme.colors.textSecondary }}>${plan.annualPrice} AUD</strong> per year
                      </p>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        background: '#10B98120',
                        border: '1px solid #10B98140',
                        borderRadius: '8px',
                        padding: '3px 10px',
                      }}>
                        <span style={{ color: '#10B981', fontSize: '12px', fontWeight: 600 }}>
                          Save {plan.annualDiscount}%
                        </span>
                      </div>
                    </>
                  ) : (
                    <p style={{ color: theme.colors.textTertiary, fontSize: '12px', margin: 0 }}>
                      Billed monthly · Cancel anytime
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul style={{
                  listStyle: 'none', padding: 0, margin: '0 0 24px',
                  display: 'flex', flexDirection: 'column', gap: '12px',
                }}>
                  {plan.features.map((f) => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: theme.colors.brand, fontSize: '16px', flexShrink: 0 }}>✓</span>
                      <span style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>{f}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  variant={plan.recommended ? 'primary' : 'secondary'}
                  onClick={() => handleSubscribe(priceId)}
                  disabled={!!isLoading}
                  style={{ width: '100%', padding: '14px', fontSize: '15px' }}
                >
                  {loading ? '⏳ Loading...' : `Start Free Trial →`}
                </Button>
              </div>
            );
          })}
        </div>

        {/* Trust signals */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '32px',
          flexWrap: 'wrap',
          marginBottom: '80px',
          padding: '24px',
          background: theme.colors.bgSecondary,
          borderRadius: '12px',
          border: `1px solid ${theme.colors.border}`,
        }}>
          {[
            '✅ No charge for 7 days',
            '✅ Cancel anytime in Settings',
            '🔒 Secured by Stripe',
            '🇦🇺 Australian hosted',
            '🚫 No data mining',
          ].map((item) => (
            <span key={item} style={{ color: theme.colors.textSecondary, fontSize: '13px' }}>
              {item}
            </span>
          ))}
        </div>

        {/* FAQ */}
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '32px', fontWeight: 700,
            color: theme.colors.textPrimary, marginBottom: '32px', textAlign: 'center',
          }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {[
              {
                q: 'How does the free trial work?',
                a: "Enter your card details to start your 7-day free trial. You won't be charged anything until the trial ends. After 7 days, your card is automatically charged for your chosen plan.",
              },
              {
                q: 'Can I cancel during the trial?',
                a: "Yes! Cancel any time before the trial ends and you won't be charged a thing. You can cancel from your Settings page.",
              },
              {
                q: 'What is the difference between monthly and annual billing?',
                a: "Monthly billing lets you pay month by month with no long-term commitment. Annual billing is paid upfront once a year and gives you a discount of 15-25% depending on your plan.",
              },
              {
                q: 'Can I switch plans later?',
                a: "Yes — you can upgrade or downgrade your plan at any time from your Settings page. Changes take effect at the next billing cycle.",
              },
              {
                q: 'What happens to my files if I cancel?',
                a: 'When you cancel your subscription, your account and all associated files are permanently deleted immediately. Please ensure you download any files you wish to keep before cancelling.',
              },
              {
                q: 'Is my payment information secure?',
                a: "Absolutely. We use Stripe for payment processing, trusted by millions of businesses worldwide. We never store your credit card information.",
              },
            ].map(({ q, a }) => (
              <Card key={q}>
                <div style={{ padding: '24px' }}>
                  <h4 style={{ color: theme.colors.textPrimary, marginBottom: '8px', fontSize: '18px' }}>{q}</h4>
                  <p style={{ color: theme.colors.textSecondary, margin: 0, lineHeight: 1.6 }}>{a}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        marginTop: '80px', padding: '40px', textAlign: 'center',
        borderTop: `1px solid ${theme.colors.border}`,
      }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '14px' }}>
          © 2026 Trubonn Systems. All rights reserved.
        </p>
        <div style={{ marginTop: '16px', display: 'flex', gap: '24px', justifyContent: 'center' }}>
          <a href="/privacy" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Privacy Policy
          </a>
          <a href="/terms" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Terms of Service
          </a>
          <a href="/contact" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>
            Contact
          </a>
        </div>
      </footer>
    </div>
  );
}
